import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { IonicModule } from '@ionic/angular';

import { NavigationService } from '@core/services/navigation/navigation.service';
import { AccountHideType, HideAccountRequest } from '@core/services/products/account-visibility.service';
import { HideAccountService } from '@core/services/products/hide-account.service';
import { UserDataService } from '@core/services/user-data/user-data.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { Product, ProductTypes } from '@shared/models/products.model';
import { SharedModule } from '@shared/shared.module';

@Component({
    selector: 'app-hide-account-inline',
    templateUrl: './hide-account-inline.component.html',
    styleUrls: ['./hide-account-inline.component.scss'],
    imports: [CommonModule, IonicModule, FormsModule, SharedModule]
})
export class HideAccountInlineComponent implements OnInit {
  @Input() product!: Product;

  @Output() toggleChange = new EventEmitter<boolean>();

  private readonly navigationService = inject(NavigationService);
  private readonly userDataService = inject(UserDataService);
  private readonly hideAccountService = inject(HideAccountService);

  // Пытаемся получить product из инжектора (если передан через NgComponentOutlet)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly injectedProduct = inject<Product | null>('PRODUCT_DATA' as any, { optional: true });
  private initialized = false;

  // Состояние компонента
  protected isEnabled = false; // Мягкое скрытие установлено (hiddenAndPass)
  protected isChangeForbidden = false; // Запрещено изменять настройки (forbiddenChange)
  protected isHardHidden = false; // Жёсткое скрытие установлено (hiddenNotPass)

  public isSubmitting = false;

  get accountNumber(): string {
    return this.product?.number || this.product?.formattedNumber || this.product?.id || '';
  }

  ngOnInit(): void {
    // Если product не передан через @Input, пытаемся получить из инжектора
    if (!this.product && this.injectedProduct) {
      this.product = this.injectedProduct;
    }

    // Проверяем, что это счет или накопительный счет
    if (
      this.product &&
      (this.product.productType === ProductTypes.ACCOUNT ||
        this.product.productType === ProductTypes.SAVE_ACCOUNT ||
        this.product.productType === ProductTypes.METAL_ACCOUNT ||
        this.product.productType === ProductTypes.DEPOSIT)
    ) {
      if (!this.initialized) {
        this.initializeFromProduct();
        this.initialized = true;
      }
    }
  }

  protected async onToggleChange(_event: CustomEvent): Promise<void> {
    // При любой попытке изменения состояния - переходим на страницу настройки скрытия
    if (!this.product) {
      return;
    }

    const hasRestrictPasswd = TypeUtilService.isTruthy(this.userDataService.userData.hasRestrictPasswd);

    if (hasRestrictPasswd) {
      try {
        this.isSubmitting = true;
        const request: HideAccountRequest = {
          accId: this.product.id,
          hideType: AccountHideType.SOFT_HIDE,
          isAccount: this.product.productType !== ProductTypes.CARD
        };

        await this.hideAccountService.hideAccount(request, this.product);
      } catch (error) {
        console.error('Ошибка при скрытии счета:', error);
      } finally {
        this.isSubmitting = false;
      }
    } else {
      try {
        const product = this.product;

        // Навигация на страницу soft-hide-account (дочерний роутинг restrictions), передаем продукт через state
        await this.navigationService.push(`product/${product.id}/restrictions/soft-hide-account`, {
          state: { product }
        });

        this.isEnabled = false;
      } catch (error) {
        console.error('Ошибка открытия страницы скрытия счёта:', error);
        this.isEnabled = false;
      }
    }
  }

  /**
   * Инициализация состояния из данных продукта
   */
  private initializeFromProduct(): void {
    if (!this.product) {
      return;
    }

    // hiddenAndPass - мягкое скрытие с паролем
    this.isEnabled = TypeUtilService.isTruthy(this.product.hiddenAndPass);

    // forbiddenChange - запрет на изменение параметров
    this.isChangeForbidden = TypeUtilService.isTruthy(this.product.forbiddenChange);

    // hiddenNotPass - жёсткое скрытие
    this.isHardHidden = TypeUtilService.isTruthy(this.product.hiddenNotPass);

    console.log('HideAccountInline Debug:', {
      productId: this.product.id,
      hiddenAndPass: this.product.hiddenAndPass,
      forbiddenChange: this.product.forbiddenChange,
      hiddenNotPass: this.product.hiddenNotPass,
      isEnabled: this.isEnabled,
      isChangeForbidden: this.isChangeForbidden,
      isHardHidden: this.isHardHidden
    });
  }
}
